import React, { useMemo, useCallback } from 'react'

import Button from '@react/react-spectrum/Button'
import FieldLabel from '@react/react-spectrum/FieldLabel'
import OverlayTrigger from '@react/react-spectrum/OverlayTrigger'
import Textfield from '@react/react-spectrum/Textfield'
import Tooltip from '@react/react-spectrum/Tooltip'

import Help from '@react/react-spectrum/Icon/Help'

import Multifield from 'components/Multifield'
import Picker from 'components/Picker'
import ProductPicker from 'components/ProductPicker'

import { useProducts, useScreens } from 'hooks'

import './ScreenPicker.scss'

const ScreenPicker = ({
  multiple,
  allowExternal,
  allowProduct,
  shouldFormatAnchors,
  allowAnchors,
  limit,
  filter,
  value: _value,
  disabled,
  allowDuplicates,
  onChange,
}) => {
  const { screens } = useScreens()
  const { getProduct } = useProducts()

  const value = useMemo(() => {
    if (!shouldFormatAnchors || !allowAnchors || !Array.isArray(_value)) {
      return _value
    }

    return _value.map((v) => {
      if (typeof v === 'string' || !v?.value || !v?.anchor?.component) {
        return v
      }

      return `${v.value}#${v.anchor.component}`
    })
  }, [shouldFormatAnchors, allowAnchors, _value])

  const { options, anchorMap } = useMemo(() => {
    const getLabel = (screen) => {
      const category = getProduct(screen.meta.product)?.category
      return (screen.meta.title || screen.meta.name) + (category ? ' / ' + category : '')
    }
    const _anchorMap = {}

    let _options = screens
      .reduce((acc, screen) => {
        if (filter ? !filter(screen) : !screen) {
          return acc
        }

        const { name, anchors: _anchors } = screen.meta || {}

        const label = getLabel(screen)
        const value = name
        const anchors = _anchors ? Object.entries(_anchors) : []

        return [
          ...acc,
          {
            label,
            value,
            children: allowAnchors
              ? anchors.reduce((acc, [component, anchor]) => {
                  const { label: anchorLabel, id: anchorId = '' } =
                    typeof anchor === 'string' ? { label: anchor, id: anchor } : anchor

                  if ((!anchorLabel && !anchorId) || !component) {
                    return acc
                  }
                  const commonLabelProps = {
                    isAnchor: true,
                    childLabel: anchorLabel || anchorId,
                    parentLabel: label,
                  }
                  const mapValue = value + '#' + component // we do not actually use anchor id here because it does not preserve the value
                  const _value = value + '#' + anchorId
                  if (shouldFormatAnchors) {
                    _anchorMap[mapValue] = {
                      value,
                      anchor: { label: anchorLabel, id: anchorId, component },
                      fullValue: _value,
                    }
                  }
                  return [
                    ...acc,
                    {
                      label: renderItem({ className: 'ScreenPicker__chosenAnchorLabel', ...commonLabelProps }),
                      ...commonLabelProps,
                      value: shouldFormatAnchors ? mapValue : _value,
                    },
                  ]
                }, [])
              : undefined,
          },
        ]
      }, [])
      .sort((a, b) => (a.label || '').localeCompare(b.label))
      .flatMap((s) => (s.children ? [s, ...s.children] : [s]))

    if (!multiple) {
      if (allowExternal) {
        _options = _options.concat({ label: '> External page', value: 'external:' })
      }
      if (!filter) {
        _options = _options.concat({ label: '> Email', value: 'mailto:' })
        _options = _options.concat({ label: ' --- ', value: '' })
      }
    }

    return { options: _options, anchorMap: _anchorMap }
  }, [allowExternal, allowAnchors, shouldFormatAnchors, filter, multiple, screens, getProduct])

  const handleChange = useCallback(
    (newValues) => {
      if (!shouldFormatAnchors || !allowAnchors) {
        return onChange(newValues)
      }

      const _newValues = newValues.map((v) => {
        const valueKey = v?.fullValue || v?.value || v
        if (anchorMap[valueKey]) {
          return anchorMap[valueKey]
        }

        return { value: valueKey }
      })

      return onChange(_newValues)
    },
    [onChange, shouldFormatAnchors, allowAnchors, anchorMap]
  )

  const commonProps = {
    value,
    options,
    disabled,
    onChange: handleChange,
    renderItem,
  }

  if (!multiple) {
    let _value = value
    if (allowProduct && value?.indexOf('?productId') > -1) _value = value?.substring(0, value?.indexOf('?productId'))
    if (value?.startsWith('external:')) _value = 'external:'
    if (value?.startsWith('mailto:')) _value = 'mailto:'

    const showProductPicker = allowProduct && _value !== 'external:' && _value !== 'mailto:'

    const productId = value?.match(/productId=([a-zA-Z0-9_]*)/g)?.[0]?.substring(10)

    const setProductId = (id) => {
      onChange(_value + '?productId=' + id)
    }

    return (
      <div style={{ display: 'flex', flexDirection: 'column' }}>
        <Picker {...commonProps} value={_value} />

        {showProductPicker && (
          <div style={{ position: 'relative' }}>
            <FieldLabel label='Open as product page for'>
              <ProductPicker value={productId} onChange={setProductId} disabled={disabled} />
            </FieldLabel>
            <OverlayTrigger placement='right'>
              <Button className='tooltip-btn' variant='tool' icon={<Help size='XS' />} />
              <Tooltip variant='info'>
                If the selected screen DOES NOT have a fixed product assigned, it will display data for product selected
                in this field.
              </Tooltip>
            </OverlayTrigger>
          </div>
        )}
        {allowExternal && value?.startsWith('external:') && (
          <>
            <Textfield
              value={value?.replace('external:', '')}
              onChange={(v) => onChange('external:' + v)}
              style={{ marginTop: '0.5rem' }}
              placeholder='http://example.com'
            />
          </>
        )}
        {allowExternal && value?.startsWith('mailto:') && (
          <>
            <Textfield
              value={value?.replace('mailto:', '')}
              onChange={(v) => onChange('mailto:' + v)}
              style={{ marginTop: '0.5rem' }}
              placeholder='example@adobe.com'
            />
          </>
        )}
      </div>
    )
  }
  return <Multifield {...commonProps} type='dropdown' limit={limit} allowDuplicates={allowDuplicates} />
}

function renderItem({ isAnchor, childLabel, label, parentLabel, className }) {
  if (!isAnchor) return label

  return (
    <span className={className}>
      <span className='ScreenPicker__parentLabel'>{parentLabel}</span>
      <span className='ScreenPicker__anchorLabel'>{childLabel}</span>
    </span>
  )
}

ScreenPicker.defaultProps = {
  multiple: false,
  currentScreen: null,
  id: null,
  limit: null,
  filter: null,
  disabled: false,
  allowAnchors: false,
  shouldFormatAnchors: false,
}

export default ScreenPicker
